import {
  CloseButton,
  HStack,
  Stack,
  Box,
  Input,
  InputGroup,
  IconButton,
  Badge,
  useDisclosure,
  Drawer,
  VStack,
  Portal,
} from '@chakra-ui/react';
import { FiSearch, FiFilter } from 'react-icons/fi'
import { useIsMobile } from '../../hooks/useIsMobile'

interface FilterBarProps {
  search?: {
    value: string
    onChange: (v: string) => void
    placeholder?: string
    onSubmit?: () => void
  }
  filterCount?: number
  children?: React.ReactNode
  actions?: React.ReactNode
}

export function FilterBar({ search, filterCount = 0, children, actions }: FilterBarProps) {
  const isMobile = useIsMobile()
  const sheet = useDisclosure()

  const searchInput = search && (
    <InputGroup maxW={{ base: 'full', md: '320px' }} startElement={<FiSearch color="gray" />}>
      <Input
        value={search.value}
        placeholder={search.placeholder ?? 'Search...'}
        onChange={(e) => search.onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && search.onSubmit) search.onSubmit() }}
        // 44px touch target on mobile only; desktop uses the natural input height
        // so it doesn't tower over adjacent size="sm" filter controls.
        minH={{ base: '44px', md: 'auto' }}
      />
    </InputGroup>
  )

  if (isMobile) {
    return (
      <Stack gap={3} mb={4}>
        <HStack gap={2}>
          {searchInput}
          {children && (
            <Box position="relative">
              <IconButton
                aria-label="Open filters"
                variant="outline"
                onClick={sheet.onOpen}
                minH="44px"
                minW="44px"><FiFilter /></IconButton>
              {filterCount > 0 && (
                <Badge
                  bg="info.subtle" color="info.fg" borderRadius="full" position="absolute" top="-6px" right="-6px"
                  fontSize="0.7rem" px={1.5}
                >
                  {filterCount}
                </Badge>
              )}
            </Box>
          )}
        </HStack>
        {actions && <Box>{actions}</Box>}

        <Drawer.Root placement='bottom' open={sheet.open} onOpenChange={e => {
          if (!e.open) {
            sheet.onClose();
          }
        }}>
          <Portal>

            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content borderTopRadius="lg">
                <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
                <Drawer.Header>Filters</Drawer.Header>
                <Drawer.Body pb="env(safe-area-inset-bottom, 16px)">
                  <VStack align="stretch" gap={4}>{children}</VStack>
                </Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>

          </Portal>
        </Drawer.Root>
      </Stack>
    );
  }

  return (
    <HStack gap={3} mb={4} align="center" flexWrap="wrap">
      {searchInput}
      {children}
      {actions}
    </HStack>
  );
}
